import React, { type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@empowernow/bff-auth-react';
import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@empowernow/ui';
import { getPluginManifests, loadPlugin } from '../plugins/registry';
import type { LoadedPlugin, PluginManifest } from '../plugins/types';
import { PluginErrorBoundary } from '../plugins/ErrorBoundary';
import { usePermittedPluginRoutes } from '../plugins/dynamicNav';
import { PluginProvider } from '../plugins/PluginContext';
import { ExperienceLauncherSection } from '../core/tiles';
import type { AppTileData } from '../core/tiles';
import { useQuickAccess } from '../contexts/QuickAccessContext';
import { selectRecentPaths } from './homeRecents';
import { useFavoritePlugins, migrateLegacyFavorites } from '../hooks/useFavoritePlugins';
import { useContributions, type ContributionBadge } from '../hooks/useContributions';
import { NeedsYouRail } from '../core/contributions/NeedsYouRail';
import { ColdStartEmptyState, CATALOG_ROUTE } from './ColdStartEmptyState';
import { EmpowerIdHomeHero } from '../components/EmpowerIdHomeHero';
import { isDedicatedBrandTheme } from '../core/theme/brandOwnership';
import { useTheme } from '../contexts/ThemeContext';
import YourStatsStrip from '../core/monitoring/YourStatsStrip';

type SlotName = 'home.hero' | 'home.quick' | 'home.main' | 'home.side' | 'home.footer' | 'sticky.bottom-right';

type SlotBuckets = {
  'home.hero': Array<{ key: string; render: () => JSX.Element }>;
  'home.quick': Array<{ key: string; render: () => JSX.Element }>;
  'home.main': Array<{ key: string; render: () => JSX.Element }>;
  'home.side': Array<{ key: string; render: () => JSX.Element }>;
  'home.footer': Array<{ key: string; render: () => JSX.Element }>;
  'sticky.bottom-right': Array<{ key: string; render: () => JSX.Element }>;
};

/**
 * Recent apps for the Home grid, derived from QuickAccessContext — the single
 * canonical recency store shared with the top-right launcher/search popup.
 * Returns navigation paths ordered most-recent-first, filtered to the freshness
 * window so stale entries fall off automatically.
 */
function useRecents(excludePaths?: ReadonlySet<string>): string[] {
  const { items } = useQuickAccess();
  return React.useMemo(
    () => selectRecentPaths(items, Date.now(), undefined, excludePaths),
    [items, excludePaths],
  );
}

/**
 * Home favorites facade over the canonical plugin-id pin store
 * (`empowernow:pinned-plugins`, shared with the GlobalLauncher). Home tiles and
 * sections are path-keyed, so this maps id<->path against the permitted routes
 * and runs the one-time legacy (path-keyed) migration once the route map exists.
 */
function useHomeFavorites(routes: Array<{ path: string; pluginId: string }>): {
  favorites: Set<string>;
  toggleFavorite: (path: string) => void;
} {
  const { pinnedIds, togglePin, refresh } = useFavoritePlugins();

  const pathToPluginId = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const r of routes) m.set(r.path, r.pluginId);
    return m;
  }, [routes]);

  const migratedRef = React.useRef(false);
  React.useEffect(() => {
    if (migratedRef.current || routes.length === 0) return;
    migratedRef.current = true;
    if (migrateLegacyFavorites(pathToPluginId)) refresh();
  }, [routes.length, pathToPluginId, refresh]);

  const favorites = React.useMemo(() => {
    const s = new Set<string>();
    for (const r of routes) if (pinnedIds.has(r.pluginId)) s.add(r.path);
    return s;
  }, [routes, pinnedIds]);

  const toggleFavorite = React.useCallback((path: string) => {
    const id = pathToPluginId.get(path);
    if (id) togglePin(id);
  }, [pathToPluginId, togglePin]);

  return { favorites, toggleFavorite };
}

/**
 * Lazy widget component - loads plugin bundle on-demand
 */
function LazyWidget({ 
  pluginId, 
  manifest, 
  componentName, 
  queryClient 
}: { 
  pluginId: string; 
  manifest: PluginManifest; 
  componentName: string; 
  queryClient: any; 
}) {
  const [plugin, setPlugin] = React.useState<LoadedPlugin | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const loaded = await loadPlugin(pluginId, queryClient);
        if (cancelled) return;
        setPlugin(loaded);
        setLoading(false);
      } catch (error) {
        if (!cancelled) {
          console.error(`[Widget] ${pluginId}: failed to load`, error);
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [pluginId, queryClient]);

  if (loading) {
    return <div style={{padding: '16px', textAlign: 'center', opacity: 0.6}}>Loading widget...</div>;
  }

  if (!plugin) {
    console.warn(`[Widget] ${pluginId}: plugin not loaded`);
    return null;
  }

  // BACKWARD COMPATIBILITY: Try widgets first, then fall back to routes
  const Component = plugin.widgets?.[componentName as keyof typeof plugin.widgets] 
    ?? plugin.routes?.[componentName as keyof typeof plugin.routes];
  
  if (!Component) {
    console.warn(`[Widget] ${pluginId}: component '${componentName}' not found in widgets or routes`);
    return null;
  }

  if (!plugin.sdk) {
    console.warn(`[Widget] ${pluginId}: SDK not available`);
    return null;
  }

  return (
    <PluginErrorBoundary>
      <PluginProvider 
        value={{ 
          pluginId: plugin.id, 
          manifest: plugin.manifest, 
          sdk: plugin.sdk 
        }}
      >
        <Component />
      </PluginProvider>
    </PluginErrorBoundary>
  );
}

function bucketizeHomeWidgets(manifests: PluginManifest[], queryClient: any): SlotBuckets {
  const buckets: SlotBuckets = {
    'home.hero': [],
    'home.quick': [],
    'home.main': [],
    'home.side': [],
    'home.footer': [],
    'sticky.bottom-right': []
  };

  for (const manifest of manifests) {
    const contribs = manifest.contributions?.widgets || [];
    for (const w of contribs) {
      const slot = w.slot as SlotName;
      if (!slot || !(slot.startsWith('home.') || slot.startsWith('sticky.'))) continue;
      
      const key = `${manifest.id}:${w.component}`;
      const render = () => (
        <LazyWidget
          pluginId={manifest.id}
          manifest={manifest}
          componentName={w.component}
          queryClient={queryClient}
        />
      );
      buckets[slot].push({ key, render });
    }
  }
  return buckets;
}

function Section({
  title,
  children,
  hidden,
  sectionTestId,
}: {
  title?: string;
  children: React.ReactNode;
  hidden?: boolean;
  /** Guide Me / ETL tours */
  sectionTestId?: string;
}) {
  if (hidden) return null;
  return (
    <section style={{ marginBottom: 16 }} aria-label={title} data-testid={sectionTestId}>
      {title && (
        <h2 style={{ 
          marginTop: 0, 
          marginBottom: 10, 
          color: 'var(--section-title-color, var(--main-text))',
          fontSize: 16,
          fontWeight: 600,
          letterSpacing: '-0.01em'
        }}>
          {title}
        </h2>
      )}
      {children}
    </section>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const dedicatedBrand = isDedicatedBrandTheme(theme);
  // LayoutShell already maintains periodic route refresh; keep Home stable.
  const { routes: permittedRoutes, loading: routesLoading } = usePermittedPluginRoutes(0);
  const [buckets, setBuckets] = React.useState<SlotBuckets>({ 'home.hero': [], 'home.quick': [], 'home.main': [], 'home.side': [], 'home.footer': [], 'sticky.bottom-right': [] });
  // Favorites first so Recent can dedup against pinned apps (a favorited app
  // never also appears in the Recent row).
  const { favorites, toggleFavorite } = useHomeFavorites(permittedRoutes);
  const recents = useRecents(favorites);
  const [stats, setStats] = React.useState<any>(null);
  const {
    badgesByPlugin,
    view: contribView,
    loading: contribLoading,
    connectionState: contribConn,
    dataStale: contribStale,
  } = useContributions(true);

  const queryClient = useQueryClient();

  // Fetch dashboard stats (lightweight)
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch('/api/tasks/unified/stats', {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          if (!cancelled) setStats(data);
        }
      } catch (err) {
        // Silently fail - stats are supplementary
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // DISABLED: Widget loading - keeping simple app tiles only
  // React.useEffect(() => {
  //   let cancelled = false;
  //   (async () => {
  //     try {
  //       const manifests = await getPluginManifests();
  //       if (cancelled) return;
  //       console.log('[Home] Loaded manifests for widgets:', manifests.length);
  //       const buckets = bucketizeHomeWidgets(manifests, queryClient);
  //       setBuckets(buckets);
  //     } catch (err) {
  //       console.error('[Home] Failed to load widgets:', err);
  //       setBuckets({ 'home.hero': [], 'home.quick': [], 'home.main': [], 'home.side': [], 'home.footer': [], 'sticky.bottom-right': [] });
  //     }
  //   })();
  //   return () => { cancelled = true; };
  // }, [queryClient]);


  const { user } = useAuth();
  // Personalized greeting from the canonical session identity (N3). First name
  // only; falls back to a name-less greeting when the session has no name.
  const firstName = React.useMemo(() => {
    const raw = (user?.name || '').trim();
    if (!raw) return '';
    // Never personalize with machine identifiers (ARN / issuer|sub / URLs)
    const low = raw.toLowerCase();
    if (
      low.startsWith('http://') ||
      low.startsWith('https://') ||
      low.startsWith('auth:') ||
      low.includes('%3a//') ||
      (raw.includes('|') && (raw.includes('/') || low.includes('realms')))
    ) {
      return '';
    }
    const token = raw.split(/\s+/)[0];
    return /@/.test(token) ? '' : token;
  }, [user?.name]);

  const greeting = (() => {
    const h = new Date().getHours();
    const timeGreeting = h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
    return firstName ? `${timeGreeting}, ${firstName}` : timeGreeting;
  })();

  const hasTasks = stats && stats.pending_count > 0;

  return (
    <div style={{ padding: '16px 24px 24px' }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>

        <EmpowerIdHomeHero greeting={greeting} />

        {/* COMPACT HEADER — hidden when dedicated brand hero is shown */}
        {!dedicatedBrand && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: 10
        }}>
          <div>
            <h1 style={{ 
              fontSize: 24, 
              fontWeight: 700, 
              margin: '0 0 1px',
              color: 'var(--main-text)',
              letterSpacing: '-0.02em'
            }}>
              {greeting}
            </h1>
            <p style={{ 
              fontSize: 12, 
              color: 'var(--main-text-secondary)',
              margin: 0
            }}>
              Choose an app to get started, or use Ctrl+K to search
            </p>
          </div>

          {hasTasks && (
            <div style={{
              padding: '8px 16px',
              borderRadius: 8,
              background: 'var(--card-bg)',
              border: '1px solid var(--input-border)',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <span style={{ color: 'var(--main-accent)', fontSize: 20, fontWeight: 700 }}>
                {stats.pending_count}
              </span>
              <span style={{ fontSize: 13, color: 'var(--main-text-secondary)' }}>
                {stats.pending_count === 1 ? 'task pending' : 'tasks pending'}
              </span>
            </div>
          )}
        </div>
        )}

        {/* NEEDS YOU — pushed contributions (badges + notifications), realtime via SSE.
            Always mounted, but the rail self-minimizes to a slim "all caught up"
            strip when nothing is pending (Okta-style), expanding as items arrive. */}
        <div style={{ marginBottom: 16 }}>
          <NeedsYouRail
            view={contribView}
            loading={contribLoading}
            connectionState={contribConn}
            dataStale={contribStale}
            onNavigate={(url) => navigate(url)}
            onSeeAll={() => navigate('/task-center')}
          />
        </div>

        {/* YOUR STATS — user-pinned living KPIs (capability-gated, live sparklines) */}
        <YourStatsStrip />

        {/* APPS - THE STAR OF THE SHOW */}
        <Section title="Your apps" sectionTestId="home-apps-section">
          {routesLoading ? (
            <div>
              {/* Loading skeleton with WOW factor! */}
              <div style={{
                textAlign: 'center',
                padding: '16px 0',
                marginBottom: 16
              }}>
                <div style={{
                  fontSize: 14,
                  color: 'var(--main-text-secondary)',
                  marginBottom: 12,
                  animation: 'pulse 2s ease-in-out infinite'
                }}>
                  Loading your apps...
                </div>
                <div style={{
                  width: 40,
                  height: 40,
                  margin: '0 auto',
                  border: '3px solid var(--border-color)',
                  borderTopColor: 'var(--main-accent)',
                  borderRightColor: 'var(--main-accent)',
                  borderRadius: '50%',
                  animation: 'spin 1s cubic-bezier(0.4, 0, 0.2, 1) infinite',
                  boxShadow: '0 0 12px var(--shadow-colored, rgba(108,76,255,0.2))'
                }} />
              </div>
              
              <div className="exp-app-grid" style={{ marginBottom: 24 }}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                  <div
                    key={i}
                    style={{
                      height: 68,
                      background: 'var(--card-bg)',
                      border: '1px solid var(--card-border, var(--input-border))',
                      borderRadius: 'var(--card-radius, 8px)',
                      padding: '12px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      position: 'relative',
                      overflow: 'hidden',
                      opacity: 0,
                      animation: `fadeInUp 0.4s ease-out ${i * 0.08}s forwards`
                    }}
                  >
                    <div style={{
                      width: 36,
                      height: 36,
                      borderRadius: 'var(--radius-md, 8px)',
                      background: 'var(--border-color)',
                      flexShrink: 0,
                      animation: `pulse 1.5s ease-in-out ${i * 0.1}s infinite`
                    }} />
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{
                        height: 14,
                        width: '65%',
                        background: 'var(--border-color)',
                        borderRadius: 4,
                        animation: `pulse 1.5s ease-in-out ${i * 0.1 + 0.1}s infinite`
                      }} />
                      <div style={{
                        height: 10,
                        width: '40%',
                        background: 'var(--border-color)',
                        borderRadius: 3,
                        opacity: 0.6,
                        animation: `pulse 1.5s ease-in-out ${i * 0.1 + 0.2}s infinite`
                      }} />
                    </div>
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, var(--card-hover-bg, rgba(255,255,255,0.04)), transparent)',
                      animation: `shimmer 2s ease-in-out ${i * 0.15}s infinite`,
                      pointerEvents: 'none'
                    }} />
                  </div>
                ))}
              </div>
            </div>
          ) : permittedRoutes.length === 0 ? (
            <ColdStartEmptyState onBrowse={() => navigate(CATALOG_ROUTE)} />
          ) : (
            <LauncherGrid
              routes={permittedRoutes}
              recents={recents}
              favorites={favorites}
              badgesByPlugin={badgesByPlugin}
              onNavigate={(p) => navigate(p)}
              onToggleFavorite={toggleFavorite}
              stats={stats}
            />
          )}
        </Section>

        {/* Widgets disabled - keeping simple app tiles only */}
      </div>

      {/* Widgets disabled - no sticky widgets */}
      {false && buckets['sticky.bottom-right'].length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 1000,
        }}>
          {buckets['sticky.bottom-right'].map(w => (
            <div key={w.key}>{w.render()}</div>
          ))}
        </div>
      )}

      {/* Command Palette delegated to GlobalLauncher (LayoutShell level) */}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideDown {
          from { 
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function groupBy<T, K extends string>(items: T[], key: (t: T) => K): Record<K, T[]> {
  return items.reduce((acc, it) => {
    const k = key(it);
    (acc[k] ||= [] as any).push(it);
    return acc;
  }, {} as Record<K, T[]>);
}

type RouteItem = { path: string; label: string; group?: string; icon?: string; description?: string; pluginId?: string };

/** Cap displayed counts so a runaway number never breaks the tile layout. */
function formatBadgeCount(count: number): string {
  return count > 99 ? '99+' : String(count);
}

/**
 * Fold a plugin's aggregated badge into tile fields. User-audience badges show an
 * exact count; entitlement/group badges degrade to an attention dot (never a
 * group count). Zero-count user badges render nothing.
 */
function applyBadge(tile: AppTileData, badge: ContributionBadge | undefined, label: string): AppTileData {
  if (!badge) return tile;
  if (badge.mode === 'dot') {
    return { ...tile, badgeMode: 'dot', badgeAriaLabel: `${label} needs your attention` };
  }
  if (badge.count > 0) {
    return {
      ...tile,
      badge: formatBadgeCount(badge.count),
      badgeMode: 'count',
      badgeAriaLabel: `${badge.count} item${badge.count === 1 ? '' : 's'} need attention in ${label}`,
    };
  }
  return tile;
}

function toAppTileData(r: RouteItem, badges?: Map<string, ContributionBadge>): AppTileData {
  const label = r.label.replace(/^\s*\p{Emoji_Presentation}\s*/u, '');
  const base: AppTileData = {
    path: r.path,
    label,
    group: r.group,
    icon: r.icon,
    description: r.description,
  };
  const badge = r.pluginId && badges ? badges.get(r.pluginId) : undefined;
  return applyBadge(base, badge, label);
}

function LauncherGrid({ routes, recents, favorites, badgesByPlugin, onNavigate, onToggleFavorite }: {
  routes: RouteItem[];
  recents: string[];
  favorites: Set<string>;
  badgesByPlugin: Map<string, ContributionBadge>;
  onNavigate: (path: string) => void;
  onToggleFavorite: (path: string) => void;
  stats: any;
}) {
  const byGroup = React.useMemo(() => groupBy(routes, r => (r.group || 'Apps') as string), [routes]);
  const favoriteRoutes = routes.filter(r => favorites.has(r.path));
  const recentRoutes = recents.map(p => routes.find(r => r.path === p)).filter(Boolean) as RouteItem[];
  const mapTiles = (items: RouteItem[]) => items.map(r => toAppTileData(r, badgesByPlugin));

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      {favoriteRoutes.length > 0 && (
        <ExperienceLauncherSection
          title="Favorites"
          variant="favorites"
          apps={mapTiles(favoriteRoutes)}
          favorites={favorites}
          onNavigate={onNavigate}
          onToggleFavorite={onToggleFavorite}
        />
      )}

      {recentRoutes.length > 0 && (
        <ExperienceLauncherSection
          title="Recent"
          variant="recent"
          apps={mapTiles(recentRoutes.slice(0, 6))}
          favorites={favorites}
          maxVisible={6}
          onNavigate={onNavigate}
          onToggleFavorite={onToggleFavorite}
        />
      )}

      {Object.entries(byGroup).map(([group, items]) => (
        <ExperienceLauncherSection
          key={group}
          title={group}
          variant="category"
          apps={mapTiles(items)}
          favorites={favorites}
          onNavigate={onNavigate}
          onToggleFavorite={onToggleFavorite}
        />
      ))}
    </div>
  );
}

